/** The Portal shows every date as dd/mm/yyyy, whatever the language or the browser's locale. */

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const pad = (n: number) => String(n).padStart(2, '0');

/**
 * A calendar day (`YYYY-MM-DD`) or an instant (ISO timestamp or Date) as dd/mm/yyyy; instants use
 * the browser's time zone. Empty values give `fallback`.
 */
export function formatDate(value: string | Date | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  if (typeof value === 'string') {
    const day = ISO_DAY.exec(value);
    if (day) return `${day[3]}/${day[2]}/${day[1]}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

/** An instant as dd/mm/yyyy HH:mm in the browser's time zone. */
export function formatDateTime(value: string | Date | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return `${formatDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** dd/mm/yyyy (also d/m/yyyy) as `YYYY-MM-DD`, or null when it is not a real day. */
export function parseDate(text: string): string | null {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text.trim());
  if (!match) return null;
  const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  )
    return null;
  return `${year}-${pad(month)}-${pad(day)}`;
}
